import React from 'react';
import { Wrench, SpinnerGap, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import LanguagesPicker from '../LanguagesPicker';

export default function ProfileTab({
  profileData, setProfileData, skills, savingProfile, handleSaveProfile,
  languagesSpoken, setLanguagesSpoken
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile Information</h2>
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} className="mt-2" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input id="phone" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input id="location" value={profileData.location} onChange={(e) => setProfileData({...profileData, location: e.target.value})} className="mt-2" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="trade">Primary Trade</Label>
              <Input id="trade" value={profileData.trade} onChange={(e) => setProfileData({...profileData, trade: e.target.value})} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="experience">Years of Experience</Label>
              <Input id="experience" value={profileData.experience} onChange={(e) => setProfileData({...profileData, experience: e.target.value})} className="mt-2" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="hourlyRate">Hourly Rate</Label>
              <Input id="hourlyRate" value={profileData.hourlyRate} onChange={(e) => setProfileData({...profileData, hourlyRate: e.target.value})} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="availability">Availability</Label>
              <Input id="availability" value={profileData.availability} onChange={(e) => setProfileData({...profileData, availability: e.target.value})} className="mt-2" />
            </div>
          </div>

          <div>
            <Label htmlFor="bio">Bio</Label>
            <Textarea id="bio" value={profileData.bio} onChange={(e) => setProfileData({...profileData, bio: e.target.value})} rows={4} className="mt-2" />
          </div>

          <div className="flex justify-end space-x-4">
            <Button variant="outline">Cancel</Button>
            <Button onClick={handleSaveProfile} className="bg-blue-600 hover:bg-blue-700" disabled={savingProfile} data-testid="save-profile-btn">
              {savingProfile ? (
                <><SpinnerGap size={20} weight="bold" className="mr-2 animate-spin" />Saving...</>
              ) : (
                <><FloppyDisk size={16} weight="bold" className="mr-2" />Save Changes</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Skills & Certifications</h3>
          <Button variant="outline" size="sm">
            <Wrench size={16} weight="bold" className="mr-2" />
            Add Skill
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => (
            <Badge key={skill} className="bg-blue-100 text-blue-700 px-3 py-1">{skill}</Badge>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6" data-testid="languages-section">
        <div className="flex justify-between items-start mb-1">
          <h3 className="text-xl font-bold text-gray-900">Languages Spoken</h3>
          {languagesSpoken.length > 0 && (
            <span className="text-xs font-bold text-neutral-500" data-testid="languages-count">
              {languagesSpoken.length} selected
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Pick the languages you can communicate in on the job. Clients can filter pros by language.
        </p>
        <LanguagesPicker value={languagesSpoken} onChange={setLanguagesSpoken} testidPrefix="worker-lang" />
        <div className="flex justify-end mt-4">
          <Button onClick={handleSaveProfile} className="bg-blue-600 hover:bg-blue-700" disabled={savingProfile} data-testid="save-languages-btn">
            {savingProfile ? (
              <><SpinnerGap size={20} weight="bold" className="mr-2 animate-spin" />Saving...</>
            ) : (
              <><FloppyDisk size={16} weight="bold" className="mr-2" />Save Languages</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
